/**
 * Chegada ao perfil por um link de oferta do WhatsApp (OFR-21).
 *
 * O link é `/{slug}?secao=assinaturas` (ou `produtos`). Âncora `#` não rola no
 * App Router, e as seções só existem depois que planos e produtos chegam da
 * API — por isso a rolagem espera o elemento aparecer, até um limite.
 *
 * A URL é lida no efeito, por `window.location`, como `useBooking` já faz com
 * `utm_source`: `useSearchParams` exigiria uma fronteira de Suspense na página
 * inteira só para ler um parâmetro uma vez.
 */

import { useEffect, useRef } from "react";
import { trackPublicFunnelEvent } from "../../../services/public";

/** As seções do perfil que um link de oferta pode abrir. */
export const PROFILE_OFFER_SECTIONS = ["assinaturas", "produtos"] as const;
export type ProfileOfferSection = (typeof PROFILE_OFFER_SECTIONS)[number];

/** Quanto tempo esperar a seção aparecer antes de desistir. */
export const OFFER_SECTION_WAIT_MS = 5000;
const OFFER_SECTION_POLL_MS = 100;

/** A seção pedida na URL, ou `null` quando não há ou não é uma das conhecidas. */
export function readOfferSection(search: string): ProfileOfferSection | null {
  const valor = new URLSearchParams(search).get("secao");
  return (PROFILE_OFFER_SECTIONS as readonly string[]).includes(valor || "")
    ? (valor as ProfileOfferSection)
    : null;
}

/**
 * Rola até a seção de `?secao=` quando ela aparecer. Parâmetro inválido,
 * ausente ou seção que não chega em 5 s (unidade sem planos ou sem produtos):
 * a página fica onde abriu, sem erro.
 */
export function useScrollToOfferSection() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const secao = readOfferSection(window.location.search);
    if (!secao) return;

    let restantes = Math.ceil(OFFER_SECTION_WAIT_MS / OFFER_SECTION_POLL_MS);
    let timer: ReturnType<typeof setInterval> | null = null;

    const tentar = () => {
      const alvo = document.getElementById(secao);
      if (alvo) {
        alvo.scrollIntoView?.({ behavior: "smooth", block: "start" });
        return true;
      }
      return false;
    };

    if (tentar()) return;
    timer = setInterval(() => {
      restantes -= 1;
      if (tentar() || restantes <= 0) {
        if (timer) clearInterval(timer);
        timer = null;
      }
    }, OFFER_SECTION_POLL_MS);

    return () => {
      if (timer) clearInterval(timer);
    };
  }, []);
}

/**
 * OFR-19: com `utm_medium=oferta`, grava `offer_opened` uma vez por
 * carregamento, com a campanha (`utm_campaign`) e a seção do link. É o dado do
 * relatório da Fase 2: quantas ofertas foram abertas, e de onde.
 *
 * A guarda é por instância: o efeito roda duas vezes no modo estrito do React
 * e de novo a cada render, e o evento não pode contar em dobro.
 */
export function useOfferOpenedTracking(slug: string | null | undefined) {
  const enviadoPara = useRef<string | null>(null);

  useEffect(() => {
    if (!slug || typeof window === "undefined") return;
    if (enviadoPara.current === slug) return;
    const params = new URLSearchParams(window.location.search);
    if ((params.get("utm_medium") || "").trim().toLowerCase() !== "oferta") return;
    enviadoPara.current = slug;
    trackPublicFunnelEvent(slug, {
      eventName: "offer_opened",
      metadata: {
        campaign: params.get("utm_campaign") || null,
        section: readOfferSection(window.location.search),
      },
    });
  }, [slug]);
}
